/**
 * The read-only effective-access panel (SPEC §9.1, §7.2).
 *
 * It answers "what can this person actually see", and the answer is the
 * server's — resolved by the same `resolve_effective_access` every report
 * endpoint calls, then intersected with the code registry. This component
 * derives nothing and infers nothing; if it started computing access from the
 * roles above it, the page would be showing a second opinion, which is the one
 * thing it must never be.
 *
 * It exists because grants union permissively: `all` on a department beats
 * `selected` on the same department for the same user (§7.2). That is correct
 * and it surprises people, so each report names the roles that reach it rather
 * than leaving an admin to simulate the resolution in their head.
 */

import { Lock, ShieldCheck, TriangleAlert } from 'lucide-react'

import type { EffectiveAccessPanel as Panel } from '../../lib/adminTypes'
import { Badge } from './ui'

export function EffectiveAccessPanel({ panel }: { panel: Panel }) {
  return (
    <div className="space-y-4">
      {panel.is_admin && (
        <p className="flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-rule)] px-3 py-2 text-[length:var(--text-xs)]">
          <ShieldCheck aria-hidden className="size-4 text-[var(--color-positive)]" />
          Administrator — every department and every report, restricted ones included. Roles do
          not affect this.
        </p>
      )}

      {panel.orphan_slugs.length > 0 && (
        <p
          role="alert"
          className="flex items-start gap-2 rounded-[var(--radius-md)] border border-[var(--color-negative)]/40 bg-[var(--color-negative)]/5 px-3 py-2 text-[length:var(--text-xs)]"
        >
          <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0" />
          <span>
            Granted but not in the report registry, so this user will not see{' '}
            {panel.orphan_slugs.length === 1 ? 'it' : 'them'}:{' '}
            <span className="numeric">{panel.orphan_slugs.join(', ')}</span>. The report package
            was removed, or the registry has not been reloaded.
          </span>
        </p>
      )}

      {panel.departments.length === 0 ? (
        <p className="rounded-[var(--radius-lg)] border border-dashed border-[var(--color-rule)] p-6 text-center text-[length:var(--text-sm)] text-[var(--color-ink-muted)]">
          No access at all. This user lands on the "no access" page when they sign in.
        </p>
      ) : (
        panel.departments.map((department) => (
          <section key={department.key}>
            <h3 className="flex items-center gap-2 text-[length:var(--text-sm)] font-medium">
              <span
                aria-hidden
                className="h-4 w-0.5 rounded-full"
                style={{ background: department.accent }}
              />
              {department.name}
              <span className="numeric text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
                {department.reports.length}
              </span>
            </h3>
            <ul className="mt-1.5 space-y-1">
              {department.reports.map((report) => (
                <li
                  key={report.slug}
                  className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-[var(--color-rule)] py-1.5 last:border-0"
                >
                  <span className="text-[length:var(--text-sm)]">{report.title}</span>
                  <span className="numeric text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
                    {report.slug}
                  </span>
                  {report.restricted && (
                    <Badge tone="warning">
                      <Lock aria-hidden className="mr-1 size-3" />
                      restricted
                    </Badge>
                  )}
                  <span className="ml-auto flex flex-wrap gap-1">
                    {report.via.length === 0 ? (
                      <Badge tone={panel.is_admin ? 'neutral' : 'danger'}>
                        {panel.is_admin ? 'admin' : 'no grant explains this'}
                      </Badge>
                    ) : (
                      report.via.map((explanation) => (
                        <Badge key={`${explanation.role_key}:${explanation.scope}`}>
                          {explanation.role_name} · {explanation.scope}
                        </Badge>
                      ))
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  )
}
